<template>
  <NewAppointmentDialog
    v-model="showAppointmentDialog"
    :customer="customer"
    :pet="pet"
    :showPetField="true"
    :initVetId="editingAppointment ? '' : vetService.vetID"
    @onOpen="
      () => {
        showAppointmentDialog = false
      }
    "
    @onSubmit="onAppointmentCreated"
    :appointment="editingAppointment"
  />

  <!-- Select Pet Dialog -->
  <el-dialog width="30%" v-model="selectPetDialogVisible" :title="t('selectPet')">
    <div class="flex items-center justify-center" :dir="$ctx.getDir()">
      <el-select
        filterable
        clearable
        class="w-full"
        value-key="id"
        v-model="selectedPetElSelect"
        remote
        :remote-method="searchPets"
      >
        <el-option
          v-for="option in pets"
          :key="option.id"
          :label="`${option.name} - ${option.customer?.name}`"
          :value="option"
        ></el-option>
      </el-select>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button :disabled="!selectedPetElSelect" @click="confirmSelectPet()" type="primary">{{
          t('confirm')
        }}</el-button>
      </span>
    </template>
  </el-dialog>

  <el-form
    @submit.prevent="validateSubmit('vetServiceForm')"
    ref="vetServiceForm"
    :model="vetService"
    :rules="rules"
    label-position="top"
    class="m-8 bg-white shadow-md p-4 px-10 gap-1"
    novalidate
  >
    <!-- Pet Information -->
    <h2 class="text-lg flex items-center w-full">
      <span class="border-black border-b-2">{{ t('petInfo') }}</span>
    </h2>
    <div class="px-4 w-full">
      <div class="flex gap-4">
        <div class="flex items-center gap-2">
          <span class="whitespace-nowrap">{{ t('petName') }}</span>
          <el-input style="pointer-events: none" v-model="petDisplayName" readonly disabled />
          <el-button
            v-if="!pet.id"
            type="primary"
            size="small"
            :icon="Plus"
            @click="selectPetDialogVisible = true"
            circle
          />
          <el-button
            v-if="pet.id"
            type="danger"
            size="small"
            :icon="Close"
            @click="clearPet"
            circle
          />
        </div>

        <div class="flex items-center gap-2">
          <span class="whitespace-nowrap">{{ t('dateOfBirth') }}</span>
          <el-input
            style="pointer-events: none; width: 100px"
            v-model="petDateOfBirth"
            readonly
            disabled
          />
        </div>

        <div class="flex items-center gap-2">
          <span class="whitespace-nowrap">{{ t('age') }}</span>
          <el-input style="pointer-events: none; width: 100px" v-model="petAge" readonly disabled />
        </div>

        <div class="flex items-center gap-2">
          <span class="whitespace-nowrap">{{ t('type') }}</span>
          <el-input style="pointer-events: none" v-model="pet.type" readonly disabled />
        </div>

        <div class="flex items-center gap-2">
          <span class="whitespace-nowrap">{{ t('sex') }}</span>
          <div class="el-input is-disabled" style="pointer-events: none; width: 80px; height: 32px;">
            <div class="el-input__wrapper text-[#A8ABB3]">{{ t(pet.sex) }}</div>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <span class="whitespace-nowrap">{{ t('neutered') }}</span>
          <el-checkbox v-model="pet.neutered" disabled />
        </div>
      </div>
      <div class="mt-2 flex gap-4 items-center">
        <div class="flex items-center gap-2">
          <span class="whitespace-nowrap ms-5">{{ t('owner') }}</span>
          <el-input
            style="pointer-events: none; width: 250px"
            :value="customerName"
            readonly
            disabled
          />
        </div>

        <div class="flex items-center gap-2">
          <span class="whitespace-nowrap">{{ t('phoneNumber') }}</span>
          <el-input
            style="pointer-events: none; width: 200px"
            :value="customerPhone"
            readonly
            disabled
            dir="ltr"
          />
        </div>
      </div>
    </div>

    <el-divider class="!mb-0" />

    <!-- Services Section -->
    <div class="my-2 w-full">
      <div class="flex items-center gap-2 mb-2">
        <h3 class="text-lg font-semibold">{{ t('services') }}</h3>
        <button
          class="px-3 rounded-lg bg-blue-200 hover:bg-blue-300 active:bg-blue-400 text-xl"
          type="button"
          @click="addService"
        >
          +
        </button>
      </div>

      <div
        v-for="(serviceItem, index) in vetService.extraProperties.services"
        :key="index"
        class="mb-3 p-3 border-2 border-blue-200 rounded-lg"
      >
        <div class="flex items-center justify-between mb-2">
          <h4 class="text-blue-600 font-semibold">{{ t('service') }} {{ index + 1 }}</h4>
          <el-button
            v-if="vetService.extraProperties.services.length > 1"
            type="danger"
            size="small"
            @click="removeService(index)"
            >{{ t('remove') }}</el-button
          >
        </div>

        <div class="grid gap-3" :class="{'grid-cols-2': serviceItem.priceType != 'dynamic', 'grid-cols-3': serviceItem.priceType == 'dynamic'}">
          <!-- Category -->
          <el-form-item
            :label="t('category')"
            :prop="`extraProperties.services.${index}.itemCategoryID`"
          >
            <el-select
              v-model="serviceItem.itemCategoryID"
              filterable
              clearable
              class="w-full"
              remote
              :remote-method="searchCategories"
              @change="onCategoryChange(index)"
            >
              <el-option
                v-for="category in categories"
                :key="category.id"
                :label="$ctx.locale === 'en' ? category.name : category.name"
                :value="category.id"
              ></el-option>
            </el-select>
          </el-form-item>

          <!-- Service -->
          <el-form-item
            :label="t('service')"
            :prop="`extraProperties.services.${index}.itemID`"
            :rules="[{ required: true, message: t('validation.required'), trigger: 'change' }]"
          >
            <el-select
              v-model="serviceItem.itemID"
              filterable
              clearable
              class="w-full"
              remote
              :remote-method="(query: string) => searchItems(query, index)"
              @change="(itemId: string) => onItemChange(itemId, index)"
            >
              <el-option
                v-for="item in items"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              ></el-option>
            </el-select>
          </el-form-item>

          <!-- Price (shown only for dynamic price items) -->
          <el-form-item
            v-if="serviceItem.priceType === 'dynamic'"
            :label="t('price')"
            :prop="`extraProperties.services.${index}.price`"
            :rules="[{ required: true, message: t('validation.required'), trigger: 'change' }]"
          >
            <money-input
              v-model="serviceItem.price"
              :currency="$ctx.currency"
              class="w-full"
            />
          </el-form-item>
        </div>

        <!-- Notes -->
        <el-form-item :label="t('notes')">
          <el-input
            v-model="serviceItem.notes"
            type="textarea"
            :rows="3"
            :placeholder="t('placeholders.notes')"
          />
        </el-form-item>

        <!-- Internal Notes -->
        <el-form-item :label="t('internalNotes')">
          <el-input
            v-model="serviceItem.internalNotes"
            type="textarea"
            :rows="3"
            :placeholder="t('placeholders.internalNotes')"
          />
        </el-form-item>

        <!-- Attachments -->
        <el-form-item :label="t('attachments')">
          <VetServiceAttachmentsManager
            :vet-service-id="vetService.id"
            :service-item-id="serviceItem.id"
          />
        </el-form-item>
      </div>
    </div>

    <el-divider class="!m-0" />

    <!-- Vet and Next Appointments -->
    <div class="px-4 w-full">
      <div class="flex justify-between gap-4">
        <div class="flex items-center my-1">
          <span>{{ t('vet') }}</span>
          <el-select
            filterable
            clearable
            v-model="vetService.vetID"
            class="mx-3"
            style="width: 120px"
            placeholder=" "
            :remote-method="searchVets"
          >
            <el-option
              v-for="option in vets"
              :key="option.id"
              :label="option.name"
              :value="option.id"
            ></el-option>
          </el-select>
        </div>

        <div class="flex flex-col items-end my-1">
          <div class="flex items-center gap-2 mb-2">
            <button
              type="button"
              class="w-6 h-6 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white text-sm"
              @click="openCreateAppointment"
            >+</button>
            <span>{{ t('nextAppointments') }}:</span>
          </div>

          <div
            v-for="appt in vetService.appointments"
            :key="appt.id"
            class="flex items-center gap-2 mb-1"
          >
            <span class="text-sm py-1 px-3 bg-gray-100 rounded border border-gray-200">
              {{ appt.appointmentDate ? formatDate(new Date(appt.appointmentDate)) : '' }}
              <span v-if="getVetName(appt)" class="text-gray-400">({{ getVetName(appt) }})</span>
            </span>
            <button type="button" class="p-1 text-blue-400 hover:text-blue-600" @click="openEditAppointment(appt)">
              <Icon icon="line-md:edit" width="16" />
            </button>
            <button type="button" class="p-1 text-red-400 hover:text-red-600" @click="deleteAppointment(appt)">
              <Icon icon="line-md:close-small" width="16" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit Buttons -->
    <div class="flex justify-end gap-4 mt-6">
      <el-button @click="$router.back()">{{ t('cancel') }}</el-button>
      <el-button
        type="success"
        @click="saveToCheckout('vetServiceForm')"
        :disabled="isSubmitting"
        :loading="isSubmitting"
      >{{
        t('saveToCheckout')
      }}</el-button>
      <el-button
        type="primary"
        @click="validateSubmit('vetServiceForm')"
        :disabled="isSubmitting"
        :loading="isSubmitting"
      >{{
        t('saveChanges')
      }}</el-button>
    </div>
  </el-form>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import VetService, { vetServiceI18nMessages } from '@/models/VetService'
import Pet from '@/models/Pet'
import Customer from '@/models/customer/Customer'
import Appointment from '@/models/customer/Appointment'
import { Plus, Close } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { formatPostgresDate, formatAge, formatDate } from '@/utils/date'
import NewAppointmentDialog from '@/views/appointments/NewAppointmentDialog.vue'
import User from '@/models/user/User'
import Item from '@/models/inventory/Item'
import Category from '@/models/inventory/Category'
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { createCheckoutTab } from '@/utils/checkoutHelper'
import MoneyInput from '@/components/form/MoneyInput.vue'
import VetServiceAttachmentsManager from '@/components/file-manager/VetServiceAttachmentsManager.vue'
import { v4 as uuidv4 } from 'uuid'

export default defineComponent({
  name: 'vet-service-form',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...vetServiceI18nMessages.en,
          confirm: 'Confirm',
          cancel: 'Cancel',
          saveChanges: 'Save Changes',
          remove: 'Remove',
          allServicesFilterNote: 'ALL services appear here if the item is tagged as "Vet Service"',
          petName: 'Pet Name',
          dateOfBirth: 'Date of Birth',
          age: 'Age',
          type: 'Type',
          owner: 'Owner',
          phoneNumber: 'Phone #',
          selectDateTime: 'Select date and time',
          customerContacted: 'Customer was contacted',
          createAppointment: 'Create Appointment',
          nextAppointments: 'Next Appointments',
          edit: 'Edit',
          delete: 'Delete',
          warning: 'Warning',
          confirmDeleteAppointment: 'Are you sure you want to delete this appointment?',
          appointmentDeleted: 'Appointment deleted',
          placeholders: {
            notes: 'Add notes here...',
            internalNotes: 'Add internal notes here...'
          },
          validation: {
            required: 'This field is required'
          },
          pleaseSelectAPet: 'Please select a pet',
          pleaseSelectAtLeastOneService: 'Please select at least one service',
          pleaseSelectAVet: 'Please select a vet',
          saveToCheckout: 'Save to Checkout',
          attachments: 'Attachments',
          neutered: 'Neutered'
        },
        ar: {
          ...vetServiceI18nMessages.ar,
          confirm: 'تأكيد',
          cancel: 'إلغاء',
          saveChanges: 'حفظ التغييرات',
          remove: 'إزالة',
          allServicesFilterNote: 'تظهر جميع الخدمات هنا إذا كان العنصر موسوماً بـ "خدمة بيطرية"',
          petName: 'اسم الحيوان الأليف',
          dateOfBirth: 'تاريخ الميلاد',
          age: 'العمر',
          type: 'النوع',
          owner: 'المالك',
          phoneNumber: 'رقم الهاتف',
          selectDateTime: 'اختر التاريخ والوقت',
          customerContacted: 'تم التواصل مع الزبون',
          createAppointment: 'إنشاء موعد',
          nextAppointments: 'المواعيد القادمة',
          edit: 'تعديل',
          delete: 'حذف',
          warning: 'تحذير',
          confirmDeleteAppointment: 'هل أنت متأكد من حذف هذا الموعد؟',
          appointmentDeleted: 'تم حذف الموعد',
          placeholders: {
            notes: 'أضف ملاحظات هنا...',
            internalNotes: 'أضف ملاحظات داخلية هنا...'
          },
          validation: {
            required: 'هذا الحقل مطلوب'
          },
          pleaseSelectAPet: 'الرجاء اختيار أليف',
          pleaseSelectAtLeastOneService: 'الرجاء اختيار خدمة واحدة على الأقل',
          pleaseSelectAVet: 'الرجاء اختيار طبيب بيطري',
          saveToCheckout: 'حفظ وإضافة للمبيعات',
          attachments: 'المرفقات',
          neutered: 'معقم'
        }
      }
    })

    return { t, Plus, Close }
  },

  props: {
    initVetService: {
      type: Object as PropType<VetService>,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    }
  },

  components: {
    NewAppointmentDialog,
    MoneyInput,
    VetServiceAttachmentsManager,
    Icon
  },

  data() {
    // Deep clone the vet service to avoid reference issues
    const vetService = VetService.from(this.initVetService)

    // Ensure extraProperties exists with proper structure
    if (!vetService.extraProperties) {
      vetService.extraProperties = { services: [] }
    }
    if (!vetService.extraProperties.services) {
      vetService.extraProperties.services = []
    }

    return {
      vetService,
      pet: new Pet(),
      customer: null as Customer | null,

      pets: [] as Pet[],
      selectedPetElSelect: null as Pet | null,
      selectPetDialogVisible: false,

      vets: [] as any[],

      categories: [] as any[],
      items: [] as any[],

      showAppointmentDialog: false,
      editingAppointment: undefined as Appointment | undefined,

      error: {
        title: '',
        body: ''
      },
      rules: {
        petID: [{ required: true, message: this.t('validation.required'), trigger: 'change' }]
      },
      isSubmitting: false
    }
  },

  computed: {
    petDisplayName(): string {
      return this.pet?.name || ''
    },

    petDateOfBirth(): string {
      if (!this.pet?.dateOfBirth) return ''
      return formatPostgresDate(this.pet.dateOfBirth.toString(), 'YYYY-MM-DD')
    },

    petAge(): string {
      if (!this.pet?.dateOfBirth) return ''
      return formatAge(this.pet.dateOfBirth.toString())
    },

    customerName(): string {
      return this.customer?.name || ''
    },

    customerPhone(): string {
      return this.customer?.phone || ''
    }
  },

  async mounted() {
    if (this.vetService.id) {
      if (this.vetService.items?.length) {
        this.items = this.vetService.items
      }
      if (this.vetService.itemCategories?.length) {
        this.categories = this.vetService.itemCategories
      }
    }

    // Load pet if editing
    if (this.vetService.pet) {
      this.pet = Pet.from(this.vetService.pet)
      this.selectedPetElSelect = this.pet
    }

    // Load customer - check both vetService.customer and pet.customer
    if (this.vetService.customer) {
      this.customer = Customer.from(this.vetService.customer)
    } else if (this.pet?.customer) {
      this.customer = Customer.from(this.pet.customer)
    }

    // Ensure vetService has proper structure
    if (!this.vetService.extraProperties) {
      this.vetService.extraProperties = { services: [] }
    }
    if (!this.vetService.extraProperties.services) {
      this.vetService.extraProperties.services = []
    }

    // Initialize with one service if empty or normalize existing services
    if (
      !this.vetService.extraProperties.services ||
      this.vetService.extraProperties.services.length === 0
    ) {
      this.vetService.extraProperties.services = [
        {
          id: uuidv4(),
          itemCategoryID: '',
          itemID: '',
          price: undefined,
          priceType: undefined,
          notes: '',
          internalNotes: ''
        }
      ]
    } else {
      // Normalize null values to empty strings and set priceType from items
      this.vetService.extraProperties.services = this.vetService.extraProperties.services.map(service => {
        const item = this.items.find((i: any) => i.id === service.itemID)
        return {
          id: service.id || uuidv4(),
          itemCategoryID: service.itemCategoryID || '',
          itemID: service.itemID || '',
          price: service.price || undefined,
          priceType: service.priceType || item?.priceType || undefined,
          notes: service.notes || '',
          internalNotes: service.internalNotes || ''
        }
      })
    }

    // Load vets dropdown
    await this.loadVets()

    // Ensure selected vet is in the vets array
    if (this.vetService.vet && this.vetService.vetID) {
      const existingVet = this.vets.find(v => v.id === this.vetService.vetID)
      if (!existingVet) {
        this.vets.push(this.vetService.vet)
      }
    }

    // Load categories and items for new vet service only
    if (!this.vetService.id) {
      await this.loadCategories()
      await this.loadItems()
    }
  },

  methods: {
    formatDate,

    openCreateAppointment() {
      this.editingAppointment = undefined
      this.showAppointmentDialog = true
    },

    openEditAppointment(appt: Appointment) {
      this.editingAppointment = appt
      this.showAppointmentDialog = true
    },

    async onAppointmentCreated(appointment: Appointment) {
      if (!this.vetService.appointments) {
        this.vetService.appointments = []
      }
      // If editing, replace the existing one
      const existingIndex = this.vetService.appointments.findIndex(a => a.id === appointment.id)
      if (existingIndex >= 0) {
        this.vetService.appointments[existingIndex] = appointment
      } else {
        // Link the new appointment to this vet service
        if (this.vetService.id && appointment.id) {
          appointment.vetServiceID = this.vetService.id
          try {
            await this.$http.put(`${Appointment.ENDPOINT}/${appointment.id}`, appointment)
          } catch (e) {
            // silent - appointment was created, linking is best-effort
          }
        }
        this.vetService.appointments.push(appointment)
      }
      this.editingAppointment = undefined
      this.showAppointmentDialog = false
    },

    getVetName(appt: Appointment): string {
      if (appt.optometrist && 'name' in appt.optometrist) {
        return appt.optometrist.name || ''
      }
      // Fallback: find vet name from the vets list using optometristID
      if (appt.optometristID) {
        const vet = this.vets.find(v => v.id === appt.optometristID)
        return vet?.name || ''
      }
      return ''
    },

    async deleteAppointment(appt: Appointment) {
      try {
        await ElMessageBox.confirm(
          this.t('confirmDeleteAppointment'),
          this.t('warning'),
          {
            confirmButtonText: this.t('confirm'),
            cancelButtonText: this.t('cancel'),
            type: 'warning'
          }
        )
      } catch {
        return
      }
      try {
        await this.$http.delete(Appointment.ENDPOINT, { data: [{ id: appt.id }] })
        this.vetService.appointments = this.vetService.appointments.filter(a => a.id !== appt.id)
        ElMessage.success(this.t('appointmentDeleted'))
      } catch (error: any) {
        this.$alertModal.showDanger({ title: error.title, body: error.body })
      }
    },

    async searchCategories(query: string) {
      try {
        const url = `${Category.ENDPOINT}?q=${query || ''}`
        const response = await this.$http.get<any[]>(url)
        this.categories = response.map(cat => Category.from(cat))
      } catch (error: any) {
        this.$alertModal.showDanger({ title: error.title, body: error.body })
      }
    },

    async searchItems(query: string, index: number) {
      try {
        const service = this.vetService.extraProperties.services[index]
        let url = `${Item.ENDPOINT}/vet-service-items-lookup`

        const params = []
        if (query) {
          params.push(`q=${query}`)
        }
        if (service.itemCategoryID) {
          params.push(`category_id=${service.itemCategoryID}`)
        }

        if (params.length > 0) {
          url += `?${params.join('&')}`
        }

        const response = await this.$http.get<any[]>(url)
        // Preserve currently selected items and merge with new results
        const selectedItemIds = this.vetService.extraProperties.services
          .map(s => s.itemID)
          .filter(Boolean)
        const selectedItems = this.items.filter((i: any) => selectedItemIds.includes(i.id))
        const newItems = response.filter((item: any) => !selectedItemIds.includes(item.id))
        this.items = [...selectedItems, ...newItems]
      } catch (error: any) {
        this.$alertModal.showDanger({ title: error.title, body: error.body })
      }
    },

    async searchVets(query: string) {
      try {
        const url = `${User.ENDPOINT}/vets?q=${query || ''}`
        const response = await this.$http.get<any[]>(url)
        this.vets = response
      } catch (error: any) {
        this.$alertModal.showDanger({ title: error.title, body: error.body })
        this.vets = []
      }
    },

    async loadCategories() {
      await this.searchCategories('')
    },

    async loadItems() {
      await this.searchItems('', 0)
    },

    async loadVets() {
      await this.searchVets('')
    },

    async onCategoryChange(index: number) {
      // Clear selected item when category changes
      this.vetService.extraProperties.services[index].itemID = undefined
      // Reload items for the selected category
      await this.searchItems('', index)
    },

    onItemChange(itemId: string, index: number) {
      const service = this.vetService.extraProperties.services[index]

      if (!itemId) {
        // Clear priceType and price when item is cleared
        service.priceType = undefined
        service.price = undefined
        return
      }

      const selectedItem = this.items.find(item => item.id === itemId)
      if (selectedItem) {
        // Set priceType from the selected item
        service.priceType = selectedItem.priceType

        // Set default price to item's sale price for dynamic pricing
        if (selectedItem.priceType === 'dynamic') {
          service.price = selectedItem.salePriceAmount || undefined
        } else {
          service.price = undefined
        }

        // Auto-select category if item has one
        if (selectedItem.itemCategoryID) {
          service.itemCategoryID = selectedItem.itemCategoryID
          // Ensure the category is in the categories list
          if (!this.categories.find(c => c.id === selectedItem.itemCategoryID)) {
            if (selectedItem.itemCategory) {
              this.categories.push(selectedItem.itemCategory)
            }
          }
        }
      }
    },

    addService() {
      this.vetService.extraProperties.services.push({
        id: uuidv4(),
        itemCategoryID: '',
        itemID: '',
        price: undefined,
        priceType: undefined,
        notes: '',
        internalNotes: ''
      })
    },

    removeService(index: number) {
      this.vetService.extraProperties.services.splice(index, 1)
    },

    async searchPets(query: string) {
      try {
        const url = `${Pet.ENDPOINT}?q=${query || ''}`
        const response = await this.$http.get<Pet[]>(url)
        this.pets = response.map((pet) => Pet.from(pet))
      } catch (error: any) {
        this.$alertModal.showDanger({ title: error.title })
        this.pets = []
      }
    },

    confirmSelectPet() {
      if (this.selectedPetElSelect) {
        this.pet = this.selectedPetElSelect
        this.vetService.petID = this.pet.id || ''
        this.vetService.pet = this.pet

        // Set customer from pet
        if (this.pet.customer) {
          this.customer = Customer.from(this.pet.customer)
          this.vetService.customerID = this.customer.id || ''
          this.vetService.customer = this.customer
        }

        this.selectPetDialogVisible = false
      }
    },

    clearPet() {
      this.pet = new Pet()
      this.customer = null
      this.vetService.petID = ''
      this.vetService.pet = null
      this.vetService.customerID = ''
      this.vetService.customer = null
    },

    async validateSubmit(formName: string) {
      if (this.isSubmitting) {
        return false
      }

      // Check if pet is selected
      if (!this.vetService.petID) {
        ElMessage.error({
          dangerouslyUseHTMLString: true,
          message: `<h2>${this.t('pleaseSelectAPet')}</h2>`
        })
        return false
      }

      // Check if at least one service is selected
      const hasService = this.vetService.extraProperties.services.some(
        service => service.itemID
      )
      if (!hasService) {
        ElMessage.error({
          dangerouslyUseHTMLString: true,
          message: `<h2>${this.t('pleaseSelectAtLeastOneService')}</h2>`
        })
        return false
      }

      // Check if vet is selected
      if (!this.vetService.vetID) {
        ElMessage.error({
          dangerouslyUseHTMLString: true,
          message: `<h2>${this.t('pleaseSelectAVet')}</h2>`
        })
        return false
      }

      const form = this.$refs[formName] as any

      form.validate(async (valid: boolean) => {
        if (valid) {
          this.isSubmitting = true
          try {
            // Ensure extraProperties.services is properly structured
            const preparedVetService = {
              ...this.vetService,
              appointmentIDs: (this.vetService.appointments || []).map(a => a.id).filter(Boolean),
              extraProperties: {
                services: this.vetService.extraProperties.services.map(service => ({
                  id: service.id,
                  itemCategoryID: service.itemCategoryID || null,
                  itemID: service.itemID,
                  price: service.price || null,
                  priceType: service.priceType || null,
                  notes: service.notes || '',
                  internalNotes: service.internalNotes || ''
                }))
              }
            }

            const error = await this.submitAction(preparedVetService)

            if (error) {
              this.error.title = error.title
              this.error.body = error.body
              this.$alertModal.showDanger({ title: error.title, body: error.body })
            } else {
              this.$alertModal.showSuccess({ title: this.successMsg })
            }
          } catch (err: any) {
            this.error.title = err.title || 'Error'
            this.error.body = err.body || 'An error occurred'
            this.$alertModal.showDanger({ title: this.error.title, body: this.error.body })
          } finally {
            this.isSubmitting = false
          }
        }
      })
    },

    async saveToCheckout(formName: string) {
      if (this.isSubmitting) {
        return false
      }

      // Check if pet is selected
      if (!this.vetService.petID) {
        ElMessage.error({
          dangerouslyUseHTMLString: true,
          message: `<h2>${this.t('pleaseSelectAPet')}</h2>`
        })
        return false
      }

      // Check if at least one service is selected
      const hasService = this.vetService.extraProperties.services.some(
        service => service.itemID
      )
      if (!hasService) {
        ElMessage.error({
          dangerouslyUseHTMLString: true,
          message: `<h2>${this.t('pleaseSelectAtLeastOneService')}</h2>`
        })
        return false
      }

      // Check if vet is selected
      if (!this.vetService.vetID) {
        ElMessage.error({
          dangerouslyUseHTMLString: true,
          message: `<h2>${this.t('pleaseSelectAVet')}</h2>`
        })
        return false
      }

      const form = this.$refs[formName] as any

      form.validate(async (valid: boolean) => {
        if (valid) {
          this.isSubmitting = true
          try {
            // Ensure extraProperties.services is properly structured
            const preparedVetService = {
              ...this.vetService,
              appointmentIDs: (this.vetService.appointments || []).map(a => a.id).filter(Boolean),
              extraProperties: {
                services: this.vetService.extraProperties.services.map(service => ({
                  itemCategoryID: service.itemCategoryID || null,
                  itemID: service.itemID,
                  price: service.price || null,
                  priceType: service.priceType || null,
                  notes: service.notes || '',
                  internalNotes: service.internalNotes || ''
                }))
              }
            }

            // Save the vet service first
            const error = await this.submitAction(preparedVetService)

            if (error) {
              this.error.title = error.title
              this.error.body = error.body
              this.$alertModal.showDanger({ title: error.title, body: error.body })
              return
            }

            // Collect services with items
            const servicesWithItems = this.vetService.extraProperties.services
              .filter(service => service.itemID)

            // Fetch items and set custom price for dynamic items
            const items = await Promise.all(
              servicesWithItems.map(async (service) => {
                try {
                  const item = await this.$http.get<Item>(`${Item.ENDPOINT}/${service.itemID}`)
                  const itemObj = Item.from(item)
                  // Override salePriceAmount for dynamic pricing
                  if (service.priceType === 'dynamic' && service.price) {
                    itemObj.salePriceAmount = service.price
                  }
                  return itemObj
                } catch (err) {
                  console.error(`Failed to fetch item ${service.itemID}:`, err)
                  return null
                }
              })
            )

            // Filter out any failed fetches
            const validItems = items.filter(item => item !== null) as Item[]

            if (validItems.length === 0) {
              this.$alertModal.showWarning({
                title: 'No Items',
                body: 'Could not fetch items for checkout'
              })
              return
            }

            // Create new checkout tab with items and customer
            createCheckoutTab(validItems, this.customer)

            // Navigate to checkout
            this.$router.push(this.$Route.SALES_NEW_SALE)
          } catch (err: any) {
            this.error.title = err.title || 'Error'
            this.error.body = err.body || 'An error occurred'
            this.$alertModal.showDanger({ title: this.error.title, body: this.error.body })
          } finally {
            this.isSubmitting = false
          }
        }
      })
    }
  }
})
</script>

<style scoped>
.border-blue-200 {
  border-color: #bfdbfe;
}
</style>
